interface ExtractedTable {
  columns: string[]
  rows: Record<string, string>[]
}

interface MarkdownTableExtraction {
  tables: ExtractedTable[]
  /** The source text with the extracted tables removed. */
  remainder: string
}

const MAX_TABLE_ROWS = 100
const MAX_TABLE_COLUMNS = 12

/** Reduce inline markdown to plain text so a table cell never shows literal `**` or link syntax. */
function plainCellText(cell: string): string {
  return cell
    .replace(/\[([^\]]+)\]\([^)]*\)/g, "$1")
    .replace(/(\*\*|__)(.+?)\1/g, "$2")
    .replace(/(^|[^*\w])[*_]([^*_]+)[*_](?=[^*\w]|$)/g, "$1$2")
    .replace(/`([^`]*)`/g, "$1")
    .trim()
}

function splitRow(line: string): string[] {
  const cells: string[] = []
  let cell = ""
  let backslashes = 0
  for (const character of line.trim()) {
    if (character === "\\") {
      cell += character
      backslashes += 1
      continue
    }
    if (character === "|") {
      if (backslashes % 2 === 1) {
        cell = `${cell.slice(0, -1)}|`
      } else {
        cells.push(cell)
        cell = ""
      }
      backslashes = 0
      continue
    }
    cell += character
    backslashes = 0
  }
  cells.push(cell)
  if (cells[0]?.trim() === "") {
    cells.shift()
  }
  if (cells.at(-1)?.trim() === "") {
    cells.pop()
  }
  return cells.map((value) => plainCellText(value))
}

function isSeparatorRow(line: string): boolean {
  const cells = splitRow(line)
  return (
    cells.length > 0 &&
    cells.every((cell) => /^:?-{3,}:?$/.test(cell.replace(/\s/g, "")))
  )
}

function isTableRow(line: string): boolean {
  return splitRow(line).length > 1
}

function uniqueColumns(columns: string[]): string[] {
  const used = new Set<string>()
  return columns.map((column, index) => {
    const base = column || `Column ${index + 1}`
    let candidate = base
    let suffix = 2
    while (used.has(candidate)) {
      candidate = `${base} (${suffix})`
      suffix += 1
    }
    used.add(candidate)
    return candidate
  })
}

interface MarkdownFence {
  marker: "`" | "~"
  length: number
}

function openingFence(line: string): MarkdownFence | null {
  const match = /^\s{0,3}(`{3,}|~{3,})/.exec(line)
  const run = match?.[1]
  if (!run) {
    return null
  }
  return { marker: run[0] as MarkdownFence["marker"], length: run.length }
}

function closesFence(line: string, fence: MarkdownFence): boolean {
  const match = /^\s{0,3}(`+|~+)\s*$/.exec(line)
  const run = match?.[1]
  return Boolean(
    run && run.startsWith(fence.marker) && run.length >= fence.length,
  )
}

/**
 * Pull GitHub-flavored markdown tables out of Sofia's prose so a bounded
 * result can render as a table cell. Sofia only ever sees bounded tool
 * payloads, so the tables are already small; the caps here are a guard, not a
 * budget.
 */
export function extractMarkdownTables(text: string): MarkdownTableExtraction {
  const lines = text.split("\n")
  const tables: ExtractedTable[] = []
  const remainderLines: string[] = []
  let index = 0
  let fence: MarkdownFence | null = null
  while (index < lines.length) {
    const line = lines[index] ?? ""
    const next = lines[index + 1] ?? ""
    if (fence) {
      remainderLines.push(line)
      if (closesFence(line, fence)) {
        fence = null
      }
      index += 1
      continue
    }
    const openedFence = openingFence(line)
    if (openedFence) {
      fence = openedFence
      remainderLines.push(line)
      index += 1
      continue
    }
    if (isTableRow(line) && isSeparatorRow(next)) {
      const columns = uniqueColumns(splitRow(line).slice(0, MAX_TABLE_COLUMNS))
      const rows: Record<string, string>[] = []
      index += 2
      while (index < lines.length && isTableRow(lines[index] ?? "")) {
        if (rows.length < MAX_TABLE_ROWS) {
          const cells = splitRow(lines[index] ?? "")
          const row: Record<string, string> = {}
          columns.forEach((column, columnIndex) => {
            row[column] = cells[columnIndex] ?? ""
          })
          rows.push(row)
        }
        index += 1
      }
      if (columns.length > 0 && rows.length > 0) {
        tables.push({ columns, rows })
      }
      continue
    }
    remainderLines.push(line)
    index += 1
  }
  return {
    tables,
    remainder: remainderLines
      .join("\n")
      .replace(/\n{3,}/g, "\n\n")
      .trim(),
  }
}
